import * as Sentry from '@sentry/react'
import axios, { AxiosRequestConfig } from 'axios'
import { StatusCallParams } from 'components/facets/StatusBlock'
import { unsetToken } from 'lib/auth'
import pako from 'pako'
import {
  Activity,
  ApiKey,
  AppCurrency,
  AppUser,
  AutopricingAssignment,
  CardAttributes,
  CardmarketAccount,
  CardmarketStatus,
  CardmarketStockAttributes,
  CardPriceGuideAttributes,
  ChangeArticlePropsWrapper,
  ChangeOrderPropsWrapper,
  ChannelFilterOptions,
  CheapestMatchesResponse,
  CreatedApiKey,
  createInventoryArticle,
  CSVImportSource,
  CustomHeaders,
  DraftPurchaseEvent,
  Game,
  GameName,
  GradingScaleByCompany,
  GradingScaleCompany,
  GradingScaleEntry,
  GradingScaleSaveResult,
  IdGame,
  InventoryArticle,
  INVENTORY_TRANSACTION_TYPE,
  JobResult,
  LinkArticleToPurchaseEvent,
  Location,
  ManagedCardTraderStock,
  MonthlySubscription,
  OrderResponseDto,
  PickingList,
  PlainCardmarketArticle,
  PlainInventoryArticle,
  PlainPricedInventoryArticle,
  PlainRichArticle,
  PowertoolsPriceTransaction,
  PowertoolsQuantityTransaction,
  PricedInventoryArticle,
  PriceReport,
  PricingPlan,
  PricingResult,
  PricingStrategy,
  Product,
  PTInventoryArticle,
  PTInventoryProduct,
  PublishChangesResponse,
  PurchaseArticle,
  PurchaseDto,
  PurchaseEvent,
  PurchaseEventDto,
  PurchaseEventPayload,
  Seller,
  sleep,
  SlimInventoryArticle,
  SortedPickingListItem,
  User,
  UserAttribute,
  UserAttributeName,
  UserSubEntity,
  UserSubEntityName,
  V2MigrationCheckState,
} from 'shared'
import { GenericSetState } from 'types'
import { CancellationToken } from 'utils/cancellationToken'
import { v4 as uuid } from 'uuid'
import zlib from 'zlib'

const apiAddress = process.env.REACT_APP_API_ADDRESS
  ? `${process.env.REACT_APP_API_ADDRESS}/api`
  : '/api'

export const sendBugReport = async (report: string): Promise<{ message: string }> => {
  return API.post(`user/report-bug`, { report })
}

export const submitBugReport = async (
  subject: string,
  description: string,
  attachments?: File[]
): Promise<{ ticket_id: number; subject: string; status: string }> => {
  const formData = new FormData()
  formData.append('subject', subject)
  formData.append('description', description)
  if (attachments) {
    attachments.forEach((file) => formData.append('attachments', file))
  }
  const response = await axios.post(`${apiAddress}/user/zendesk/tickets`, formData, {
    withCredentials: true,
  })
  return response.data
}

export const sendDevNotification = async (notification: string): Promise<{ message: string }> => {
  return API.post(`user/send-dev-notification`, { notification })
}

export const recordUsage = async (
  activity: Activity,
  details: Record<string, unknown>,
  googleTrackingData?: { category: string; value?: number; action: string }
): Promise<{ message: string }> => {
  if (googleTrackingData) window.dataLayer?.push({ event: 'app_event', ...googleTrackingData })
  return API.post(`user/usage`, { activity, details })
}

export const getCheapestMatches = async (
  article: InventoryArticle,
  competitorId: string,
  strategy: Pick<PricingStrategy, 'disablePlaysetPricing' | 'strictLanguage'>,
  idGame: IdGame
): Promise<CheapestMatchesResponse> => {
  return API.post(
    `user/pricing/cheapest-matches`,
    {
      article: article.toPlainObject(),
      competitorId,
      strategy,
    },
    { idGame }
  )
}

const compressIfBig = (data: unknown, headers: any) => {
  headers['Content-Type'] = 'application/json'

  const stringified = data ? JSON.stringify(data) : ''

  if (stringified.length > 1024 && process.env.NODE_ENV === 'production') {
    headers['Content-Encoding'] = 'gzip'
    return pako.gzip(stringified)
  } else {
    return stringified || data
  }
}

export const autopriceArticles = async (
  autopricingAssignments: AutopricingAssignment[],
  setProgress: GenericSetState,
  cancellationToken: CancellationToken
): Promise<Record<string, PriceReport>> => {
  let results: Record<string, PriceReport> = {}

  const response = await axios.post(
    apiAddress + '/user/pricing/autoprice',
    { autopricingAssignments },
    {
      withCredentials: true,
      transformRequest: compressIfBig,
    }
  )

  const jobId = response.data.jobId

  const totalArticleCount = autopricingAssignments.reduce(
    (acc, val) => acc + val.articles.length,
    0
  )

  while (Object.keys(results).length < totalArticleCount) {
    if (cancellationToken.cancelled) throw new Error('cancelled')
    await sleep(1000)
    const response = await API.get(`job/autopricing/${jobId}`)
    response.results.forEach((result: Record<string, PriceReport>) => {
      results = Object.assign(results, result)
    })
    setProgress(Math.round((Object.keys(results).length / totalArticleCount) * 100))
  }
  return results
}

// export const publishChanges = async (
//   articles: InventoryArticle[],
//   setProgress: GenericSetState,
//   idGame: IdGame
// ): Promise<PublishChangesResponse> => {
//   const result: PublishChangesResponse = []

//   let data = await API.put(
//     `user/stock`,
//     inventoryArticlesToStockChanges(articles) as Record<string, unknown>,
//     { idGame }
//   )

//   if (data.jobId) {
//     const response: any = await getDataFromJobResult(
//       data.jobId,
//       'Could not publish stock. Try again later',
//       (progress) => setProgress(progress)
//     )

//     const POSTfailed = articles
//       .filter((article) => response.POSTfailures[article.idArticle])
//       .map((article) => ({ article, error: response.POSTfailures[article.idArticle] }))

//     const DELETEfailed = articles
//       .filter((article) => response.DELETEfailures[article.idArticle])
//       .map((article) => ({ article, error: response.DELETEfailures[article.idArticle] }))

//     const PUTfailed = articles
//       .filter((article) => response.PUTfailures[article.idArticle])
//       .map((article) => ({ article, error: response.PUTfailures[article.idArticle] }))

//     const failed = POSTfailed.concat(DELETEfailed).concat(PUTfailed)

//     //@ts-ignore
//     data = {
//       failed: failed,
//       insertedCount: articles.length - failed.length,
//     }
//   }

//   if (typeof data.insertedCount !== 'number') throw new Error('Publishing failed')

//   recordUsage(Activity.PublishArticles, { count: data.insertedCount, idGame })

//   data.failed.forEach((f: { article: InventoryArticle; error: string }) => {
//     result.push({
//       article: f.article,
//       attributeChangeSuccess: false,
//       quantityChangeSuccess: false,
//       attributeError: f.error,
//       quantityError: '',
//     })
//   })

//   return result
// }

const getDataFromJobResult = async (
  jobId: string,
  errorMessage: string,
  onProgress?: (progress: number) => void
) => {
  let data: JobResult | null = null

  while (!data?.result) {
    await new Promise((r) => setTimeout(r, 2000))
    data = await API.get(`job/${jobId}`)

    onProgress?.(data?.progress || 0)
  }
  if (!data?.result) throw new Error(errorMessage)
  return data?.result
}

export const getUser = async (): Promise<AppUser> => {
  return API.get('user')
}

export const getUserStock = async (
  idGame: Game['idGame']
): Promise<{ stock: CardmarketStockAttributes[]; sealedStock: CardmarketStockAttributes[] }> => {
  let data = await API.get(`user/stock`, { idGame })
  if (data.jobId) {
    const response = await getDataFromJobResult(
      data.jobId,
      'Could not fetch stock. Try again later'
    )

    //@ts-ignore
    data = response
  }

  return data
}

export const getArticlesInShoppingCarts = async (): Promise<PlainCardmarketArticle[]> => {
  let data = await API.get(`user/stock/articlesInShoppingCarts`)
  if (data.jobId) {
    const response = await getDataFromJobResult(
      data.jobId,
      'Could not fetch articles in shopping cart. Try again later'
    )

    //@ts-ignore
    data = response
  }

  return data
}

export const saveCardmarketBackup = async (
  idGame: IdGame,
  part: number,
  fetchedStock: InventoryArticle[]
) => {
  return API.post(`user/stock/saveCardmarketBackup`, { idGame, part, fetchedStock })
}

export const finalizeCardmarketBackupBatch = async (manifest: Record<number, number>) => {
  return API.post('user/stock/zipCardmarketBackup', { manifest })
}

export const getOtherUserStock = async (
  idGame: Game['idGame'],
  isSealed = false,
  tokens: {
    key: ''
    secret: ''
  }
): Promise<CardmarketStockAttributes[]> => {
  let data
  if (isSealed === true) {
    data = await API.get(`user/stock/otherUser`, {
      idGame,
      isSealed,
      key: tokens.key,
      secret: tokens.secret,
    })
  } else {
    data = await API.get(`user/stock/otherUser`, { idGame, key: tokens.key, secret: tokens.secret })
  }
  if (data.jobId) {
    const response = await getDataFromJobResult(
      data.jobId,
      'Could not fetch stock. Try again later'
    )

    //@ts-ignore
    data = response
  }

  return data
}

const getResource = async <T>(
  id: string,
  currentVersion?: string
): Promise<
  { resourceData: T; version: string; clientIsUpToDate: false } | { clientIsUpToDate: true }
> => {
  const resourceCheck = await API.get(`resource/${id}?currentVersion=${currentVersion}`)
  if (resourceCheck.clientIsUpToDate) return { clientIsUpToDate: true }

  if (!resourceCheck.url) throw new Error(`Could not get ${id}`)

  // Cookies go only to our own hosts. Matching 'tcgpowertools' anywhere in the URL also matched the
  // signed object storage URLs (the bucket is named tcgpowertools), and storage rejects credentialed
  // CORS requests, so the browser blocked every price guide download.
  const { hostname } = new URL(resourceCheck.url, window.location.origin)
  const response = await axios.get(resourceCheck.url as string, {
    withCredentials:
      hostname === window.location.hostname ||
      hostname === 'localhost' ||
      hostname === 'tcgpowertools.com' ||
      hostname.endsWith('.tcgpowertools.com'),
  })
  const data: T = response.data

  return {
    resourceData: data,
    version: resourceCheck?.version,
    clientIsUpToDate: false,
  }
}

export const getPriceGuideMap = async (idGame: IdGame, version?: string) => {
  return await getResource<Record<number, CardPriceGuideAttributes>>(
    `priceGuide_new_${idGame}`,
    version
  )
}

export const getCardData = async (idGame: IdGame, version?: string) => {
  return await getResource<{ cards: CardAttributes[]; expansions: any }>(`data_${idGame}`, version)
}

export const getCsvProducts = async <T>(idGame: IdGame) => {
  return (await getResource(`csvProducts_new_${idGame}`)) as {
    resourceData: T
    version: string
    clientIsUpToDate: false
  }
}

export const getCurrencyRate = async (
  from: AppCurrency,
  to: AppCurrency
): Promise<{ rate: number | null }> => {
  const data = await API.get(`status/currency/${from}/${to}`)
  return data
}

export const changeSubscription = async (announcedCost: number): Promise<UserSubEntity> => {
  return API.post(`user/subscription/start`, { announcedCost })
}
export const cancelSubscription = async ({
  unsubComment,
  unsubReasons,
}: {
  unsubComment: string
  unsubReasons: string
}): Promise<any> => {
  return API.post(`user/subscription/cancel`, { unsubComment, unsubReasons })
}
export const removeCancelSubscription = async (): Promise<void> => {
  return API.delete(`user/subscription/cancel`, {})
}

export const listApiKeys = async (): Promise<ApiKey[]> => {
  return API.get(`user/api-keys`)
}

export const createApiKey = async (title: string): Promise<CreatedApiKey> => {
  return API.post(`user/api-keys`, { title })
}

export const deleteApiKey = async (id: number): Promise<void> => {
  return API.delete(`user/api-keys/${id}`)
}

export const createUserSubEntity = async (
  subEntity: Omit<UserSubEntity, '_id'>,
  subEntityName: UserSubEntityName
): Promise<Partial<User>> => {
  return API.post(`user/${subEntityName}`, { ...subEntity, _id: uuid() })
}

// Variant that accepts an explicit _id from the caller, for when the caller
// needs to know the new entity's id up front (e.g. to auto-open its editor
// right after creating a pricing plan).
export const createUserSubEntityWithId = async (
  _id: string,
  subEntity: Omit<UserSubEntity, '_id'>,
  subEntityName: UserSubEntityName
): Promise<Partial<User>> => {
  return API.post(`user/${subEntityName}`, { ...subEntity, _id })
}

export const editUserSubEntity = async (
  subEntity: UserSubEntity,
  subEntityName: UserSubEntityName
): Promise<Partial<User>> => {
  return API.put(`user/${subEntityName}`, { ...subEntity })
}

export const editUserAttribute = async (
  attribute: UserAttribute,
  attributeName: UserAttributeName
): Promise<Partial<User>> => {
  return API.put(`user`, { [attributeName]: attribute })
}

export const editUser = async (attributes: Partial<User>): Promise<User> => {
  return API.put(`user`, attributes)
}

export const changeActiveGameId = async (
  activeGameId: Game['idGame'] /**todo this could be enum */
): Promise<{ activeGameId: number }> => {
  return API.put('user/set-active-game', { activeGameId })
}

export const deleteUserSubEntity = async (
  subEntityId: UserSubEntity['_id'],
  subEntityName: UserSubEntityName
): Promise<Partial<User>> => {
  return API.delete(`user/${subEntityName}/${subEntityId}`)
}

export type CSVImportPayload = {
  csvString: string
  importSource: CSVImportSource
}

export interface CSVImportResponse {
  articles?: InventoryArticle[]
  failedArticles?: Record<string, unknown>[]
}

export const getArticlesFromCsv = async (
  payload: CSVImportPayload,
  gameName: GameName
): Promise<CSVImportResponse> => {
  return API.post(`import-csv/${gameName}`, payload)
}

export const resetAssignedAccounts = async (): Promise<User> => {
  return API.get('user/reset-assigned-accounts')
}

export const disconnectCardmarketAccount = async (): Promise<{ message: string }> => {
  return API.get('user/disconnect-cardmarket')
}

export const switchCardmarketAccount = async (username: string): Promise<Partial<User>> => {
  return API.put(`user/cardmarket/account`, { cardmarketUsername: username })
}

export const getCardmarketStatus = async (): Promise<{
  status: CardmarketStatus
  banner?: { message: string; _id: string }
}> => {
  return API.get('status/cardmarket')
}

export const getSurvey = async (): Promise<{ Id: string; PostId: string }> => {
  return API.get('user/survey')
}

export const markSurveyCompleted = async (surveyId: string): Promise<void> => {
  return API.put(`user/survey/${surveyId}`, {})
}

export const getSubscriptionOffer = async (): Promise<{
  currentSubscriptionOffer: number | null
}> => {
  const data = await API.get('user/subscription/offer')

  if (!data.jobId) return { currentSubscriptionOffer: null }

  const response = await getDataFromJobResult(data.jobId, '')

  return response as { currentSubscriptionOffer: number | null }
}

export interface SalesVolumeResponse {
  subscriptionStart: Date
  subscriptionEnd: Date
  currency: string
  salesVolumes: {
    cardmarket?: number
    cardmarketSingles?: number
    cardmarketSealed?: number
    cardtrader?: number
    cardtraderSingles?: number
    cardtraderSealed?: number
    shopify?: number
    shopifySingles?: number
    shopifySealed?: number
    total: number
  }
}

export const getSalesVolume = async (): Promise<SalesVolumeResponse> => {
  return API.get('user/subscription/sales-volume')
}

export const getAdminMonthlySubscriptions = async (userId: string): Promise<any[]> => {
  return API.get(`admin-api/monthly-subscriptions/${userId}`)
}

export const getAdminSalesVolumes = async (
  userId: string,
  subscriptionId: string
): Promise<SalesVolumeResponse> => {
  return API.get(`admin-api/monthly-subscriptions/${userId}/${subscriptionId}/sales-volumes`)
}

export const toggleAdminShopify = async (userId: string): Promise<{ shopify: boolean }> => {
  return API.put(`admin-api/toggle-shopify/${userId}`, {})
}

export const toggleAdminTaxation = async (
  userId: string
): Promise<{ taxation: boolean; addedFields: string[] }> => {
  return API.put(`admin-api/toggle-taxation/${userId}`, {})
}

export const moveToV2 = async (userId: string): Promise<{ success: boolean }> => {
  return API.put(`admin-api/move-to-v2/${userId}`, {})
}

export const getPricingBotBetaStatus = async (
  userId: string
): Promise<{ enabled: boolean; hasPricingPlans: boolean; hasBackup: boolean }> => {
  return API.get(`admin-api/pricing-bot-beta-status/${userId}`)
}

export const enablePricingBotBeta = async (
  userId: string
): Promise<{ success: boolean; plansCount: number }> => {
  return API.post(`admin-api/enable-pricing-bot-beta/${userId}`, {})
}

export const disablePricingBotBeta = async (
  userId: string
): Promise<{
  success: boolean
  restoredCount: number
  keptCount: number
  hadBackup: boolean
}> => {
  return API.post(`admin-api/disable-pricing-bot-beta/${userId}`, {})
}

export const wipePowertoolsInventory = async (userId: string): Promise<{ success: boolean }> => {
  return API.post(`admin-api/wipe-powertools-inventory/${userId}`, {})
}

// MKM Products - Admin
export const getMkmProducts = async (params: {
  page: number
  search?: string
  idGame?: string
  expansionId?: string
  categoryName?: string
  shouldBeUpdated?: string
  cardtraderId?: 'with' | 'without'
  sortBy?: string
  sortDirection?: string
}): Promise<any[]> => {
  const query = new URLSearchParams()
  query.set('page', String(params.page))
  if (params.search) query.set('search', params.search)
  if (params.idGame) query.set('idGame', params.idGame)
  if (params.expansionId) query.set('expansionId', params.expansionId)
  if (params.categoryName) query.set('categoryName', params.categoryName)
  if (params.shouldBeUpdated) query.set('shouldBeUpdated', params.shouldBeUpdated)
  if (params.cardtraderId) query.set('cardtraderId', params.cardtraderId)
  if (params.sortBy) query.set('sortBy', params.sortBy)
  if (params.sortDirection) query.set('sortDirection', params.sortDirection)
  return API.get(`admin-api/mkm-products?${query.toString()}`)
}

export const getMkmProductChangelogs = async (mkmProductId: number): Promise<any[]> => {
  return API.get(`admin-api/mkm-product-changelogs?mkmProductId=${mkmProductId}`)
}

export const getMkmExpansionChangelogs = async (mkmExpansionId: number): Promise<any[]> => {
  return API.get(`admin-api/mkm-expansion-changelogs?mkmExpansionId=${mkmExpansionId}`)
}

export const updateUserEmail = async (
  userId: string,
  newEmail: string
): Promise<{ success: boolean }> => {
  return API.put(`admin-api/update-email/${userId}`, { newEmail })
}

export const updateUserCurrency = async (
  userId: string,
  currency: string
): Promise<{ success: boolean }> => {
  return API.put(`admin-api/update-currency/${userId}`, { currency })
}

export const unlinkCardtrader = async (userId: string): Promise<{ success: boolean }> => {
  return API.delete(`admin-api/unlink-cardtrader/${userId}`)
}

export interface CardtraderLaunchStatus {
  pendingTransactions: number
  onboardingTransactions: number
  syncRunning: boolean
  alignmentRunning: boolean
  blocked: boolean
  lastAlignmentStart: string | null
  lastAlignmentEnd: string | null
  lastSyncStart: string | null
  lastSyncEnd: string | null
}

export interface UserStockSummaryGame {
  idGame: number
  uniqueArticles: number
  totalItems: number
  soldItems: number
  singlesValue: number
  sealedValue: number
  totalValue: number
}

export interface UserStockSummary {
  currency: string
  totals: { stockValue: number; uniqueArticles: number; totalItems: number; soldItems: number }
  gameBreakdown: UserStockSummaryGame[]
}

export const getUserStockSummary = async (userId: string): Promise<UserStockSummary> => {
  return API.get(`admin-api/stock-summary/${userId}`)
}

export const getCardtraderLaunchStatus = async (
  userId: string
): Promise<CardtraderLaunchStatus> => {
  return API.get(`admin-api/cardtrader-pending-transactions/${userId}`)
}

export const runCardtraderAlignment = async (
  userId: string
): Promise<{ launched: boolean } & Partial<CardtraderLaunchStatus>> => {
  return API.post(`admin-api/cardtrader-alignment/${userId}`, {})
}

export const runCardtraderSync = async (
  userId: string
): Promise<{ launched: boolean } & Partial<CardtraderLaunchStatus>> => {
  return API.post(`admin-api/cardtrader-sync/${userId}`, {})
}

export const startProductCatalogUpdate = async (payload: {
  productId?: number
  expansionId?: number
  withImages: boolean
}): Promise<{ expansionId: number }> => {
  return API.post(`admin-api/product-catalog-update`, payload)
}

export const runCardmarketOrderFetch = async (
  userId: string,
  limitDate?: string
): Promise<{ success: boolean }> => {
  return API.post(`admin-api/cardmarket-order-fetch/${userId}`, { limitDate })
}

export const getAdminCardmarketAccounts = async (
  userId: string
): Promise<{ username: string; professional: boolean; currency: string }[]> => {
  return API.get(`admin-api/cardmarket-accounts/${userId}`)
}

// Kicks off the Cardmarket stock export for a user and returns the job to follow with
// getJobStatus. The CSV itself is fetched with downloadAdminCardmarketStockExport once the job
// reports 'completed'.
export const startAdminCardmarketStockExport = async (
  userId: string,
  idGame: number
): Promise<{ jobId: string }> => {
  return API.post(`admin-api/cardmarket-stock-export/${userId}?idGame=${idGame}`, {})
}

export const downloadAdminCardmarketStockExport = async (
  userId: string,
  jobId: string,
  idGame: number
) => {
  return axios.get(
    `${apiAddress}/admin-api/cardmarket-stock-export/${userId}/${jobId}?idGame=${idGame}`,
    {
      withCredentials: true,
      responseType: 'blob',
    }
  )
}

export interface AdminTransactionsExport {
  id: number
  intervalStartDate: string
  intervalEndDate: string
  createdAt: string
  exportEndedAt: string | null
  lastErrorAt: string | null
  lastError: string | null
  params: { channel: string; exportType: string }
}

export const getAdminTransactionsExports = async (
  userId: string,
  page: number
): Promise<{ transactionCsvExports: AdminTransactionsExport[]; hasNextPage: boolean }> => {
  return API.get(`admin-api/transactions-export/${userId}?page=${page}`)
}

export const startAdminTransactionsExport = async (
  userId: string,
  from: string,
  to: string,
  channel: string,
  exportType: string
): Promise<AdminTransactionsExport> => {
  return API.post(`admin-api/transactions-export/${userId}`, { from, to, channel, exportType })
}

export const getAdminTransactionsExport = async (
  exportId: number
): Promise<
  | { status: 'pending' }
  | { status: 'ready'; transactionCsvExport: AdminTransactionsExport; csvData: string }
> => {
  return API.get(`admin-api/transactions-export/download/${exportId}`)
}

// The "check migration to v2" run goes on on the server: start it, then read where it got to.
export const startV2MigrationCheck = async (userId: string): Promise<{ started: boolean }> => {
  return API.post(`admin-api/v2-migration-check/${userId}`, {})
}

export const getV2MigrationCheck = async (
  userId: string
): Promise<{ state: V2MigrationCheckState | null; running: boolean }> => {
  return API.get(`admin-api/v2-migration-check/${userId}`)
}

export const downloadV2MigrationCheckZip = async (userId: string) => {
  return axios.get(`${apiAddress}/admin-api/v2-migration-check/${userId}/zip`, {
    withCredentials: true,
    responseType: 'blob',
  })
}

export const unlinkCardmarket = async (
  userId: string,
  account: string
): Promise<{ success: boolean }> => {
  return API.post(`admin-api/unlink-cardmarket/${userId}`, { account })
}

// Changelog - Admin
export const getAdminChangelog = async (): Promise<ChangelogEntry[]> => {
  return API.get('admin-api/changelog')
}

export const createChangelogEntry = async (entry: {
  title: string
  titleEs: string
  titleFr: string
  titleDe: string
  titleIt: string
  body: string
  bodyEs: string
  bodyFr: string
  bodyDe: string
  bodyIt: string
  date: string
  audienceType: string
  audienceUserIds?: string[]
  audienceFeatureFlags?: string[]
}): Promise<{ id: number }> => {
  return API.post('admin-api/changelog', entry)
}

export const updateChangelogEntry = async (
  id: number,
  entry: {
    title: string
    titleEs: string
    titleFr: string
    titleDe: string
    titleIt: string
    body: string
    bodyEs: string
    bodyFr: string
    bodyDe: string
    bodyIt: string
    date: string
    audienceType: string
    audienceUserIds?: string[]
    audienceFeatureFlags?: string[]
  }
): Promise<{ id: number }> => {
  return API.put(`admin-api/changelog/${id}`, entry)
}

export const deleteChangelogEntry = async (id: number): Promise<{ success: boolean }> => {
  return API.delete(`admin-api/changelog/${id}`)
}

export interface ChangelogReader {
  userId: string
  cardmarketUsername: string
  email: string
}

export const getChangelogReaders = async (id: number): Promise<ChangelogReader[]> => {
  return API.get(`admin-api/changelog/${id}/readers`)
}

export interface ContentMediaInfo {
  id: number
  signedId: string
  filename: string
  contentType: string
  byteSize: number
  createdAt: string
}

export const uploadContentMedia = async (file: File): Promise<{ signedId: string }> => {
  const formData = new FormData()
  formData.append('image', file)
  const response = await axios.post(`${apiAddress}/admin-api/content-media`, formData, {
    withCredentials: true,
  })
  return response.data
}

export const getContentMedia = async (): Promise<ContentMediaInfo[]> => {
  return API.get('admin-api/content-media')
}

export const deleteContentMedia = async (id: number): Promise<{ success: boolean }> => {
  return API.delete(`admin-api/content-media/${id}`)
}

// Static pages - Admin
export interface StaticPage {
  id: number
  url: string
  title: string
  html: string
  htmlEs: string
  htmlFr: string
  htmlDe: string
  htmlIt: string
  draft: boolean
  createdByUserId: string
  lastUpdatedByUserId: string
  createdAt: string
  updatedAt: string
}

export type StaticPagePayload = {
  url: string
  title: string
  html: string
  htmlEs: string
  htmlFr: string
  htmlDe: string
  htmlIt: string
  draft: boolean
}

export const getStaticPages = async (): Promise<StaticPage[]> => {
  return API.get('admin-api/static-pages')
}

// Public render: fetches the localized html for a published page. Throws (404) for missing/draft
// pages and (400) for an unsupported locale.
export const getPublicStaticPage = async (
  locale: string,
  path: string
): Promise<{ html: string }> => {
  return API.get(`static-pages/${locale}/${encodeURI(path)}`)
}

export const getStaticPage = async (id: number): Promise<StaticPage> => {
  return API.get(`admin-api/static-pages/${id}`)
}

export const getApiDocs = async (version: string): Promise<string> => {
  return API.get(`user/api-keys/docs/${encodeURIComponent(version)}/html`)
}

export const getApiDocsSpec = async (version: string): Promise<Record<string, unknown>> => {
  return API.get(`user/api-keys/docs/${encodeURIComponent(version)}/spec`)
}

export const getPostmanCollection = async (version: string): Promise<Record<string, unknown>> => {
  return API.get(`user/api-keys/docs/${encodeURIComponent(version)}/postman_collection`)
}

// Authenticated preview (manage_static_pages): renders a single locale and, unlike the public
// endpoint, also serves drafts.
export const previewStaticPage = async (id: number, locale: string): Promise<{ html: string }> => {
  return API.get(`admin-api/static-pages/${id}/preview/${locale}`)
}

export const createStaticPage = async (page: StaticPagePayload): Promise<{ id: number }> => {
  return API.post('admin-api/static-pages', page)
}

export const updateStaticPage = async (
  id: number,
  page: StaticPagePayload
): Promise<{ id: number }> => {
  return API.put(`admin-api/static-pages/${id}`, page)
}

export const deleteStaticPage = async (id: number): Promise<{ success: boolean }> => {
  return API.delete(`admin-api/static-pages/${id}`)
}

// Grading scale - Backoffice
export const getGradingScale = async (): Promise<GradingScaleCompany[]> => {
  return API.get('admin-api/grading-scale')
}

export const updateGradingScale = async (
  gradingCompanyId: number,
  entries: GradingScaleEntry[],
  fingerprint: string
): Promise<GradingScaleSaveResult> => {
  return API.put(`admin-api/grading-scale/${gradingCompanyId}`, { entries, fingerprint })
}

// Changelog - User
export const getChangelog = async (): Promise<ChangelogEntry[]> => {
  return API.get('user/changelog')
}

export const markChangelogAsRead = async (id: number): Promise<{ success: boolean }> => {
  return API.post(`user/changelog/${id}/read`, {})
}

export interface ChangelogEntry {
  id: number
  title: string
  titleEs: string
  titleFr: string
  titleDe: string
  titleIt: string
  body: string
  bodyEs: string
  bodyFr: string
  bodyDe: string
  bodyIt: string
  date: string
  audienceType: string
  audienceUserIds: string[]
  audienceFeatureFlags?: string[]
  createdByUserId: string
  isRead: boolean
  createdAt: string
  updatedAt: string
}

export const getPriceUpdates = async (
  idGame: IdGame
): Promise<{
  updates: PricingResult[]
}> => {
  return API.get(`user/pricing/updates`, { idGame })
}

export const getPriceUpdateArticles = async (
  priceUpdateId: string
): Promise<(PricedInventoryArticle | InventoryArticle)[]> => {
  const { nonCompetitionArticlesLink, articlesLink } = await API.get(
    `user/pricing/update/${priceUpdateId}`
  )

  let articles: (PlainPricedInventoryArticle | PlainInventoryArticle)[] = []

  if (nonCompetitionArticlesLink) {
    const response1 = await axios.get(nonCompetitionArticlesLink)
    const nonCompetitionArticles = response1.data

    articles = [...articles, ...nonCompetitionArticles]
  }

  if (articlesLink) {
    const response1 = await axios.get(articlesLink)
    const competitionArticles = response1.data

    articles = [...articles, ...competitionArticles]
  }

  return articles.map((article) => createInventoryArticle(article))
}

export const importFile = async (rows: any, idGame: IdGame) => {
  const data = await API.post('import', { rows }, { idGame })

  if (!data.jobId) throw new Error('No jobId')

  const response = (await getDataFromJobResult(data.jobId, '')) as {
    parsedArticles: PlainRichArticle[]
    failedArticles: Record<string, unknown>[]
  }

  response.parsedArticles = response.parsedArticles.map((article) => new InventoryArticle(article))

  return response as {
    parsedArticles: InventoryArticle[]
    failedArticles: Record<string, unknown>[]
  }
}

export const runTestPriceUpdate = async (
  pricingPlanId: string,
  idGame: IdGame
): Promise<{
  jobId: string
}> => {
  return API.post(`user/pricing/test-run`, { pricingPlanId }, { idGame })
}

export const runManualPriceUpdate = async (
  pricingPlanId: string,
  idGame: IdGame
): Promise<{
  jobId: string
}> => {
  return API.post(`user/pricing/manual-run`, { pricingPlanId }, { idGame })
}

// The plan converted as the new pricing bot would show it. Nothing is saved
export const previewPricingBotBeta = async (pricingPlanId: string): Promise<PricingPlan> => {
  return API.get(`user/pricing/beta/preview/${pricingPlanId}`)
}

// Moves every pricing program of the user to the new pricing bot
export const activateMyPricingBotBeta = async (): Promise<{
  success: boolean
  plansCount: number
}> => {
  return API.post(`user/pricing/beta/enable`, {})
}

export const getCurrentVersion = async (): Promise<{ version: string }> => {
  return await axios.get(`${process.env.REACT_APP_API_ADDRESS || ''}/meta.json`)
}

export const changeBotActivation = async (
  account: CardmarketAccount,
  idGame: IdGame,
  planId: string | 'none'
): Promise<{ assignedCardmarketAccounts: CardmarketAccount[] }> => {
  if (planId === 'none') {
    return await API.delete(`user/pricingBots`, { account: account.username, idGame })
  } else {
    return await API.post(`user/pricingBots`, { account: account.username, planId, idGame })
  }
}

export const changePricingBotActivation = async (
  idGame: IdGame,
  planId: string | 'none'
): Promise<{ pricingBotActivations: { idGame: IdGame; planId: string }[] }> => {
  if (planId === 'none') {
    return await API.delete(`user/pricingBotActivations`, { idGame })
  }
  return await API.post(`user/pricingBotActivations`, { idGame, planId })
}

export const setUserOutsetaAttributes = (payload: {
  emailPreferences?: {
    gettingStarted: boolean
    productNews: boolean
    surveys: boolean
  }
  howDidYouFindOut?: string
  referralCode?: string
}): Promise<{ done: true }> => {
  return API.put('user/outseta', payload)
}

export const fetchSubscriptionGaps = (): Promise<MonthlySubscription[]> => {
  return API.get(`user/subscription/getUserSubscriptionGaps`)
}

export const getDashboardStockSummary = async (
  gameIds: number[]
): Promise<{
  stockValue: number
  uniqueArticles: number
  totalItems: number
  soldItems: number
  gameBreakdown: {
    idGame: number
    singlesValue: number
    sealedValue: number
    totalValue: number
    uniqueArticles: number
    totalItems: number
  }[]
}> => {
  const params = gameIds.map((id) => `gameIds=${id}`).join('&')
  return API.get(`user/dashboard/stockSummary?${params}`)
}

export const createStripeSession = (returnPath: string): Promise<{ sessionId: string }> => {
  return API.get(`user/billing/setup-sepa-mandate?returnPath=${returnPath}`)
}

export const setupSepaMandate = (sessionId: string): Promise<{ done: boolean }> => {
  return API.post(`user/billing/setup-sepa-mandate`, { sessionId })
}

export const getApiClient = (apiAddress: string, config: AxiosRequestConfig) => {
  return {
    get: async (endpoint: string, headers: CustomHeaders = {}) => {
      return await axios
        .get(`${apiAddress}/${endpoint}`, { ...config, headers: { ...headers, ...config.headers } })
        .then((res) => {
          return res.data
        })
        .catch((err) => {
          return Promise.reject(err)
        })
    },
    post: async (
      endpoint: string,
      payload: Record<string, unknown> | FormData,
      headers: CustomHeaders = {}
    ) => {
      return await axios
        .post(`${apiAddress}/${endpoint}`, payload, {
          ...config,
          headers: { ...headers, ...config.headers },
        })
        .then((res) => {
          return res.data
        })
        .catch((err) => {
          return Promise.reject(err)
        })
    },
    put: async (
      endpoint: string,
      payload: Record<string, unknown>,
      headers: CustomHeaders = {}
    ) => {
      return await axios
        .put(`${apiAddress}/${endpoint}`, payload, {
          ...config,
          headers: { ...headers, ...config.headers },
        })
        .then((res) => {
          return res.data
        })
        .catch((err) => {
          return Promise.reject(err)
        })
    },
    delete: async (
      endpoint: string,
      payload?: Record<string, unknown>,
      headers: CustomHeaders = {}
    ) => {
      return await axios
        .delete(`${apiAddress}/${endpoint}`, {
          ...config,
          headers: { ...headers, ...config.headers },
          data: payload,
        })
        .then((res) => {
          return res.data
        })
        .catch((err) => {
          return Promise.reject(err)
        })
    },
  } as const
}

const API = getApiClient(apiAddress, {
  withCredentials: true,
  transformRequest: compressIfBig,
})

// The JWT inside the cookie expires after a day while the cookie lasts 30: past that every call
// answers 401 and the user stays stuck in the app. Going through /signin lets Outseta log them back in.
let redirectingToSignIn = false
axios.interceptors.response.use(undefined, (error) => {
  const onAuthPage = ['/signin', '/signup'].some((path) =>
    window.location.pathname.startsWith(path)
  )
  if (error?.response?.status === 401 && !onAuthPage && !redirectingToSignIn) {
    redirectingToSignIn = true
    unsetToken()
    window.location.assign('/signin')
  }
  return Promise.reject(error)
})

// Some users received API responses whose Date header was minutes or hours older than the request
// (a stored copy served instead of a fresh answer, RAILS-5J investigation). We do not know yet why,
// so every call is checked: the tags show which methods, endpoints and pages are affected.
// The threshold leaves room for slow responses (up to ~120s) and for small clock differences.
const STALE_RESPONSE_THRESHOLD_MS = 3 * 60 * 1000
axios.interceptors.request.use((config) => {
  ;(config as AxiosRequestConfig & { sentAt?: number }).sentAt = Date.now()
  return config
})
axios.interceptors.response.use((response) => {
  const sentAt = (response.config as AxiosRequestConfig & { sentAt?: number }).sentAt
  const dateHeader = response.headers?.date
  const generatedAt = dateHeader ? Date.parse(dateHeader) : NaN
  if (sentAt && !isNaN(generatedAt) && sentAt - generatedAt > STALE_RESPONSE_THRESHOLD_MS) {
    const path = new URL(response.config.url ?? '', window.location.origin).pathname.replace(
      /\/\d+(?=\/|$)/g,
      '/:id'
    )
    Sentry.withScope((scope) => {
      scope.setLevel('warning')
      scope.setTag('method', response.config.method?.toUpperCase() ?? 'unknown')
      scope.setTag('apiPath', path)
      scope.setTag('page', window.location.pathname)
      scope.setExtra('requestSentAt', new Date(sentAt).toISOString())
      scope.setExtra('responseDate', dateHeader)
      scope.setExtra('ageSeconds', Math.round((sentAt - generatedAt) / 1000))
      scope.setExtra('durationMs', Date.now() - sentAt)
      scope.setExtra('status', response.status)
      scope.setExtra('etag', response.headers?.etag)
      Sentry.captureMessage('API response older than its request')
    })
  }
  return response
})

// backoffice status monitoring
export const getCpuStatus = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/cpu?server=${params.server}&env=${params.env}`)
}

export const getMemoryStatus = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/memory?server=${params.server}&env=${params.env}`)
}

export const getHddStatus = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/hdd?server=${params.server}&env=${params.env}`)
}

export const getQueueStatus = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/queue?queue=${params.queue}`)
}

export const getRedisMemory = async (): Promise<any> => {
  return await API.get(`backoffice/status/redisMemory`)
}

export const getMongoCollectionStatus = async (params: StatusCallParams): Promise<any> => {
  return await API.get(
    `backoffice/status/collections?collection=${params.collection}&maxValue=${params.maxValue}`
  )
}

export const getRedisCacheStatus = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/cache?maxValue=${params.maxValue}`)
}

export const getSmallestTTL = async (params: StatusCallParams): Promise<any> => {
  return await API.get(
    `backoffice/status/smallestTTL?minValue=${params.minValue}&maxValue=${params.maxValue}`
  )
}

export const getNumArticlesFetched = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/numArticlesFetched?minValue=${params.minValue}`)
}

export const getFetcherMetrics = async (): Promise<any> => {
  return await API.get(`backoffice/status/fetcherMetrics`)
}

export const getTimeLastArticleDataBackup = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/timeLastArticleDataBackup?maxValue=${params.maxValue}`)
}

export const getTimeLastProductIdsBackup = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/timeLastProductIdsBackup?maxValue=${params.maxValue}`)
}

export const getTimeLastExpansionsBackup = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/timeLastExpansionsBackup?maxValue=${params.maxValue}`)
}

export const getTimeLastCategoriesBackup = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/timeLastCategoriesBackup?maxValue=${params.maxValue}`)
}

export const getRedisCacheSize = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/redisCacheSize?minValue=${params.minValue}`)
}

export const getFetcherRuntimeMain = async (): Promise<any> => {
  return await API.get(`backoffice/status/fetcherRuntimeMain`)
}

export const getFetcherRuntimeFast = async (): Promise<any> => {
  return await API.get(`backoffice/status/fetcherRuntimeFast`)
}

export const getCountOldArticles = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/countOldArticles?maxValue=${params.maxValue}`)
}

export const get429Errors = async (): Promise<any> => {
  return await API.get(`backoffice/status/429Errors`)
}

export const getPageLoadingTime = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/pageLoad?env=${params.env}&maxValue=${params.maxValue}`)
}

export const getMongoQueryResponseTime = async (params: StatusCallParams): Promise<any> => {
  return await API.get(
    `backoffice/status/queryResponseTime?collection=${params.collection}&maxValue=${params.maxValue}`
  )
}

export const getLastProductsUpdate = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/lastProductsUpdate?maxValue=${params.maxValue}`)
}

export const getSidekiqQueueSize = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/sidekiqQueueSize?queueName=${params.queueName}`)
}

export const getNumUnprocessedTransactions = async (params: StatusCallParams): Promise<any> => {
  return await API.get(`backoffice/status/numUnprocessedTransactions?tableName=${params.tableName}`)
}

export const getNumUsersWithNullSubscriptionOffer = async (): Promise<any> => {
  return await API.get(`backoffice/status/numUsersWithNullSubscriptionOffer`)
}

export const getNumPtProductsWithNoCardtraderHandle = async (): Promise<any> => {
  return await API.get(`backoffice/status/numPtProductsWithNoCardtraderHandle`)
}

export const getNumDuplicatePtProducts = async (): Promise<any> => {
  return await API.get(`backoffice/status/numDuplicatePtProducts`)
}

export const getStalePricingBots = async (): Promise<any> => {
  return await API.get(`backoffice/status/stalePricingBots`)
}

export const getNumDuplicatePtProductTemplates = async (): Promise<any> => {
  return await API.get(`backoffice/status/numDuplicatePtProductTemplates`)
}

export const getDuplicateScryfallIds = async (): Promise<any> => {
  return await API.get(`status/duplicate-scryfall-ids`)
}

export const cleanQueue = async (
  queueName: string,
  grace: number,
  limit: number,
  type: string,
  password: string
): Promise<any> => {
  return await API.put(`backoffice/cleanQueue`, { queueName, grace, limit, type, password })
}

export const drainQueue = async (queueName: string, password: string): Promise<any> => {
  return await API.put(`backoffice/drainQueue`, { queueName, password })
}

export const articleData = async (password: string, mkmId: string): Promise<any> => {
  return await API.get(`backoffice/articleData?pwd=${password}&mkmId=${mkmId}`)
}

export const getStatusUnprocessedTransactions = async (params: StatusCallParams): Promise<any> => {
  const { transactionType } = params
  return await API.get(
    `backoffice/status/unprocessedTransactions?transactionType=${transactionType}`
  )
}

export const checkSentry = async (): Promise<any> => {
  return await API.get(`kaboom`)
}

export const fetchProduct = async (id: number): Promise<Product> => {
  return await API.get(`product/fetch?id=${id}`)
}

export const fetchProducts = async (ids: number[]): Promise<Product[]> => {
  return await API.post(`product/fetchList`, { ids })
}

// PT orders management
export const handleFetchOrders = async (userId?: string): Promise<OrderResponseDto[]> => {
  return await API.get(userId ? `user/order?userId=${userId}` : `user/order`)
}

export const changeArticleProp = async (
  idOrder: number,
  idArticle: number,
  prop: ChangeArticlePropsWrapper
): Promise<OrderResponseDto> => {
  return await API.put(`user/order/changeArticleProp`, {
    idOrder,
    idArticle,
    prop,
  })
}

export const changeOrdersState = async (
  orderId: number,
  newState: string
): Promise<OrderResponseDto> => {
  return await API.put(`user/order/changeOrdersState`, { orderId, newState })
}

export const changeOrderProp = async (prop: ChangeOrderPropsWrapper): Promise<OrderResponseDto> => {
  return await API.put(`user/order/changeOrderProp`, {
    prop,
  })
}

export const solveAllIssues = async (orderId: number, solution: string) => {
  return await API.post(`user/order/solveAllIssues`, {
    orderId,
    solution,
  })
}

export const createPickingList = async (orderIds: number[]): Promise<OrderResponseDto[]> => {
  return await API.put(`user/order/createPickingList`, { orderIds })
}

export const getUserPickingLists = async (state: string): Promise<PickingList[]> => {
  return await API.get(`user/order/getUserPickingLists?state=${state}`)
}

export const deletePickingList = async (pickingList: PickingList): Promise<OrderResponseDto[]> => {
  return await API.put(`user/order/deletePickingList`, {
    pickingList,
  })
}

export const updateSorting = async (
  pickingListId: number,
  sortingFields: string[],
  items?: SortedPickingListItem[]
): Promise<void> => {
  return API.put('user/order/updateSorting', { pickingListId, sortingFields, items })
}

export const getArticlesToPick = async (
  pickingListId: number,
  sortingFields: string[]
): Promise<any> => {
  return await API.put('user/order/getArticlesToPick', { pickingListId, sortingFields })
}

export const removeOrderFromPickingList = async (
  pickingListName: string,
  orderId: number
): Promise<OrderResponseDto> => {
  return await API.put(`user/order/removeOrderFromPickingList`, {
    pickingListName,
    orderId,
  })
}

export const forceClosePickingList = async (pickingListId: number): Promise<OrderResponseDto[]> => {
  return await API.put(`user/order/forceClosePickingList`, { pickingListId })
}

// Backoffice order transactions
export const getFullOrderTransactionsData = async (params: any): Promise<any> => {
  return await API.put(`user/order/getFullOrderTransactionsData`, { params })
}

export const getOrderTransactionPayload = async (params: any): Promise<any> => {
  return await API.put(`user/order/getOrderTransactionPayload`, { params })
}

export const getOrderRelistingsCount = async (): Promise<any> => {
  return await API.get(`user/order/getOrderRelistingsCount`, {})
}

export const getOrderRelistingsData = async (): Promise<any> => {
  return await API.get(`user/order/getOrderRelistingsData`, {})
}

export const confirmRelistingsChoice = async (userChoice: any): Promise<any> => {
  return await API.post(`user/order/confirmRelistingsChoice`, { userChoice })
}

// ebay
export const generateEbayAuthUrl = async () => {
  return await API.get(`ebay/generateEbayAuthUrl`)
}

export const getEbayAccessToken = async (encodedAuthToken: string) => {
  return await API.get(`ebay/getEbayAccessToken?encodedAuthToken=${encodedAuthToken}`)
}

export const ebayDisconnect = async () => {
  return await API.put(`ebay/ebayDisconnect`, {})
}

export const getEbayOfficialTime = async () => {
  return await API.get(`ebay/getEbayOfficialTime`)
}

export const getEbayOrders = async () => {
  return await API.get(`ebay/getEbayOrders`)
}

export const createEbayAddTask = async () => {
  return await API.get(`ebay/createEbayAddTask`)
}

export const getEbayTask = async (taskId: string) => {
  return await API.get(`ebay/getEbayTask?taskId=${taskId}`)
}

export const uploadEbayFile = async (taskId: string, fileName: string, fileContent: string) => {
  return await API.post(`ebay/uploadEbayFile`, { taskId, fileName, fileContent })
}

export const getEbayResultFile = async (taskId: string) => {
  return await API.get(`ebay/getEbayResultFile?taskId=${taskId}`)
}

export const createEbayEndTask = async () => {
  return await API.get(`ebay/createEbayEndTask`)
}

export const ebayListItems = async (articles: InventoryArticle[]) => {
  return await API.post(`ebay/ebayListItems`, { articles })
}

export const ebayRemoveItems = async (articles: InventoryArticle[]) => {
  return await API.post(`ebay/ebayRemoveItems`, { articles })
}

export const getEbayUser = async () => {
  return await API.get(`ebay/getEbayUser`)
}

// PT inventory
export const addInventoryItem = async (article: PTInventoryArticle) => {
  return await API.post(`user/inventory/addItem`, { article })
}

export const loadInventory = async (
  idGame?: number,
  userId?: string
): Promise<PTInventoryProduct[]> => {
  const allProducts: PTInventoryProduct[] = []
  let page = 0
  let totalPages = 1

  while (page < totalPages) {
    let url = `user/inventory?page=${page}`
    if (idGame) {
      url += `&idGame=${idGame}`
    }
    if (userId) {
      url += `&userId=${userId}`
    }

    const response = await API.get(url)
    allProducts.push(...response.products)
    totalPages = response.totalPages
    page++
  }

  console.log(`loadInventory: total ${allProducts.length} products`)
  return allProducts
}

export const getPtArticleLocations = async (
  ptArticleId: number,
  onlyPositiveQuantity?: boolean
): Promise<Location[]> => {
  let url = `user/inventory/getPtArticleLocations?ptArticleId=${ptArticleId}`

  if (onlyPositiveQuantity !== undefined) {
    url += `&onlyPositiveQuantity=${onlyPositiveQuantity}`
  }

  return await API.get(url)
}
// The grades a seller can pick. The backoffice reads the same table through admin-api/grading-scale,
// which also carries the invalidated rows it needs in order to bring one back.
export const getSelectableGradingScale = async (): Promise<GradingScaleByCompany> => {
  return await API.get('user/inventory/gradingScale')
}

export interface GradeRelabelRow {
  ptArticle: {
    _id: number
    comment: string | null
    baseSellPrice: number
    locations: { name: string; quantity: number }[]
  }
  // PtProductSerializer, which already speaks the shape ArticleAttributes reads.
  ptProduct: {
    _id: number
    mkmProductId: number
    idGame: number
    name: string
    cn: string
    expansionName: string
    expansionCode: string
    condition: string
    idLanguage: number
    finishType: number
    countryEdition: number
    isFirstEd: boolean
    isSigned: boolean
    gradingCompany: string
    grade: string
  }
  quantity: number
  // The grades the seller can pick for this card today, in rank order: the whole scale of its company.
  candidates: string[]
}

// completedAt is set once the seller has been through the relabel, and the rows are empty from then on.
export const getGradeRelabelRows = async (): Promise<{
  rows: GradeRelabelRow[]
  completedAt: string | null
}> => {
  return await API.get('user/inventory/gradeRelabel')
}

// grade null means the card turned out not to be graded at all.
export const confirmGradeRelabel = async (
  rows: { ptArticleId: number; grade: string | null }[]
): Promise<void> => {
  return await API.post('user/inventory/confirmGradeRelabel', { rows })
}

export const getPtProductsDetails = async (ids: number[]) => {
  return await API.get(`user/inventory/getPtProductsDetails?ids=${ids}`)
}

export const getInventoryArticles = async (params: any): Promise<PTInventoryArticle[]> => {
  return await API.post(`user/inventory/getInventoryArticles`, { params })
}

export const clearInventory = async () => {
  return await API.post(`user/inventory/clear`, {})
}

export const deleteInventoryItem = async (article: PTInventoryArticle) => {
  return await API.delete(`user/inventory/deleteItem`, { article })
}

export const editInventoryItem = async (newArticle: PTInventoryArticle) => {
  return await API.put(`user/inventory/editItem`, { newArticle })
}

export const updateInventoryItem = async (newArticle: PTInventoryArticle) => {
  return await API.put(`user/inventory/updateItem`, { newArticle })
}

export const redirect = async () => {
  return await API.post(`user/redirect`, {})
}

// PT inventory transactions
export const loadInventoryTransactions = async (params: any) => {
  return await API.post(`inventoryTransaction/`, params)
}

export const getUnprocessedTransactions = async () => {
  return await API.get(`inventoryTransaction/unprocessedTransactions`)
}

export const getInventoryStatusUpdate = async (idGame: number, maxProcessedAt?: string) => {
  if (maxProcessedAt)
    return await API.get(
      `inventoryTransaction/getInventoryStatusUpdate?idGame=${idGame}&maxProcessedAt=${encodeURIComponent(
        maxProcessedAt
      )}`
    )
  else return await API.get(`inventoryTransaction/getInventoryStatusUpdate?idGame=${idGame}`)
}

// Backoffice inventory transactions
export const getFullTransactionsData = async (params: any) => {
  return await API.put(`user/inventory/getFullTransactionsData`, { params })
}

export const writeInventoryTransactions = async (
  type: string,
  inventoryTransactions: PowertoolsQuantityTransaction[] | PowertoolsPriceTransaction[]
) => {
  return await API.post(`inventoryTransaction/write`, { type, inventoryTransactions })
}

const compress = (data: any) => {
  return zlib.deflateSync(JSON.stringify(data))
}

export const writeInventoryTransactionsAsync = async (
  powertoolsQuantityTransactions: any[],
  powertoolsTaxTransactions: any[],
  powertoolsPriceTransactions: any[],
  powertoolsTransactions: any[],
  platform: string = INVENTORY_TRANSACTION_TYPE.POWERTOOLS
) => {
  const compressed = {
    compressedPowertoolsQuantityTransactions: compress(powertoolsQuantityTransactions),
    compressedPowertoolsTaxTransactions: compress(powertoolsTaxTransactions),
    compressedPowertoolsPriceTransactions: compress(powertoolsPriceTransactions),
    compressedPowertoolsTransactions: compress(powertoolsTransactions),
    platform: platform,
  }
  return await API.post(`inventoryTransaction/writeAsync`, compressed)
}

export const processAllInventoryTransactions = async () => {
  return await API.post(`inventoryTransaction/processAll`, {})
}

export const inventoryArticlesToTransactions = async (
  user: AppUser,
  articles: SlimInventoryArticle[],
  newListings = false,
  cause: string,
  source?: string
) => {
  return await API.post(`inventoryTransaction/inventoryArticlesToTransactions`, {
    user,
    articles,
    newListings,
    cause,
    source,
  })
}

export const startPowertoolsProcessingInventoryTransactions = async () => {
  return await API.post(`inventoryTransaction/startPowertoolsProcessing`, {})
}

export const writeOnboardingTransactions = async (
  user: AppUser,
  idGame: IdGame,
  articles: SlimInventoryArticle[],
  newListings = false,
  cause: string,
  source?: string
) => {
  const data = await API.post(`inventoryTransaction/writeOnboardingTransactions`, {
    user,
    idGame,
    articles,
    newListings,
    cause,
    source,
  })

  const jobId = data.id
  return jobId
}

export const getJobStatus = async (jobId: string) => {
  return await API.get(`job/status/${jobId}`)
}

export const getJobData = async (jobId: string) => {
  return await API.get(`job/info/${jobId}`)
}

export const publishChangesToPTInventory = async (
  user: AppUser,
  articles: SlimInventoryArticle[],
  newListings = false,
  cause: string,
  source?: string
): Promise<PublishChangesResponse> => {
  return await inventoryArticlesToTransactions(user, articles, newListings, cause, source)
}

// PT reservation transactions
export const processReservationTransactions = async () => {
  return await API.post(`reservationTransaction/process`, {})
}

export const getOngoingSyncs = async () => {
  return await API.get('inventoryTransaction/ongoingSyncs')
}

export const shopifyOauthStart = async (
  hmac: string,
  host: string,
  shop: string,
  timestamp: string
) => {
  return await API.get(
    `shopify/oauth_start?hmac=${hmac}&host=${host}&shop=${shop}&timestamp=${timestamp}`
  )
}

export const shopifyOauthConclude = async (
  code: string,
  hmac: string,
  host: string,
  shop: string,
  state: string,
  timestamp: string
) => {
  return await API.get(
    `shopify/oauth_conclude?code=${code}&hmac=${hmac}&host=${host}&shop=${shop}&state=${state}&timestamp=${timestamp}`
  )
}

export const shopifyUpdatePriceIncrease = async (shop: string, modifer: number) => {
  return await API.post('shopify/shopifyUpdatePriceIncrease', { shop: shop, modifier: modifer })
}

export const shopifyConnectShop = async (userId: string, shop: string) => {
  return await API.get(`shopify/connect_shop?userId=${userId}&shop=${shop}`)
}

export interface ShopifySalesChannel {
  id: string
  name: string
  selected: boolean
}

export const shopifyGetSalesChannels = async (
  shop: string
): Promise<{ channels: ShopifySalesChannel[] }> => {
  return await API.get(`shopify/channels?shop=${shop}`)
}

export const shopifyUpdateSalesChannels = async (shop: string, publicationIds: string[]) => {
  return await API.post('shopify/update_channels', { shop: shop, publicationIds: publicationIds })
}

export const cardtraderOauthStart = async () => {
  return await API.post(`cardtrader/oauth_start`, {})
}

export const cardtraderOauthConclude = async (code: string, startAlignment: boolean) => {
  return await API.post(`cardtrader/oauth_conclude`, { code, startAlignment })
}

export const getUsersDetails = async (
  params: any
): Promise<{ data: User[]; totalCount: number }> => {
  return await API.put(`user/getUsersDetails`, { params })
}

export const startFetchingCartraderStock = async () => {
  return await API.post(`cardtrader/start_alignment`, {})
}

export const saveUpdatedCardTraderStock = async (articles: ManagedCardTraderStock[]) => {
  return await API.post(`cardtrader/conclude_alignment`, { articles })
}

export const cardtraderAlignmentStatus = async () => {
  return await API.get(`cardtrader/alignment_status`)
}

export const cardtraderAlignmentStart = async () => {
  return await API.post(`cardtrader/alignment_start`, {})
}

export const cardtraderAlignmentConfirm = async () => {
  return await API.post(`cardtrader/alignment_confirm`, {})
}

export const cardtraderGameAlignmentStatus = async (idGame?: number) => {
  const query = idGame === undefined ? '' : `?idGame=${idGame}`

  return await API.get(`cardtrader/game_alignment_status${query}`)
}

export const cardtraderGameAlignmentStart = async (idGame: number) => {
  return await API.post(`cardtrader/game_alignment_start`, { idGame })
}

export const cardtraderGameAlignmentConfirm = async (articles: ManagedCardTraderStock[]) => {
  return await API.post(`cardtrader/game_alignment_confirm`, { articles })
}

export const cardtraderAlignmentCancel = async () => {
  return await API.post(`cardtrader/alignment_cancel`, {})
}

export const backOfficeCreateNewRandomOrderTransaction = async (channel: ChannelFilterOptions) => {
  return await API.post(`user/order/createNewRandomOrderTransaction`, { channel })
}

export const backOfficeCreateFromLastOrderTransaction = async (
  channel: ChannelFilterOptions,
  cardmarketOrderId: number,
  newState: string
) => {
  return await API.post(`user/order/createFromLastOrderTransaction`, {
    channel,
    cardmarketOrderId,
    newState,
  })
}

// purchase events

export const getPurchaseEvents = async (currentPage: number) => {
  return await API.get(`taxation/getPurchaseEvents/${currentPage}`)
}

export const getPurchasesOfPurchaseEvent = async (
  purchaseEventId: number
): Promise<PurchaseDto[]> => {
  return await API.get(`taxation/getPurchasesOfPurchaseEvent?purchaseEventId=${purchaseEventId}`)
}

export const updatePurchases = async (purchases: PurchaseArticle[]) => {
  return await API.post(`taxation/updatePurchases`, { purchases })
}

export const updateSeller = async (purchaseEventId: number, seller: Partial<Seller>) => {
  return await API.post(`taxation/updateSeller`, { purchaseEventId, seller })
}

export const updatePurchaseEvent = async (lot: PurchaseEvent) => {
  return await API.post(`taxation/updatePurchaseEvent`, { lot })
}

export const createPurchaseEvent = async (
  purchaseEvent: PurchaseEventPayload
): Promise<PurchaseEventDto> => {
  return await API.post(`taxation/createPurchaseEvent`, { purchaseEvent })
}

export const deletePurchaseEvent = async (purchaseEventId: number) => {
  return await API.delete(`taxation/deletePurchaseEvent`, { purchaseEventId })
}

export const deletePurchase = async (purchaseEventId: number, purchaseId: number) => {
  return await API.delete(`taxation/deletePurchase`, { purchaseEventId, purchaseId })
}

export const linkArticlesToPurchaseEvent = async (
  purchaseEventId: number,
  products: LinkArticleToPurchaseEvent
) => {
  return await API.post(`taxation/linkArticlesToPurchaseEvent`, { purchaseEventId, products })
}

export const runSplittingStrategy = async (
  purchaseEventId: number,
  draftPurchaseEvent?: DraftPurchaseEvent
): Promise<any> => {
  return await API.post(`taxation/runSplittingStrategy`, {
    purchaseEventId,
    draftPurchaseEvent,
  })
}

export const getPurchasesLinkedToPtProduct = async (ptProductId: number) => {
  return await API.get(`taxation/getPurchasesLinkedToPtProduct?ptProductId=${ptProductId}`)
}

// amendments to the articles relating to taxation
export const updateTaxationScheme = async (article: SlimInventoryArticle) => {
  return await API.post(`taxation/updateTaxationScheme`, {
    article,
  })
}

export const fetchCsvReports = async (
  page: number,
  createdAtFrom?: string,
  createdAtTo?: string
) => {
  let url = `taxation/fetchCsvReports/${page}`
  const params = new URLSearchParams()
  if (createdAtFrom) params.set('createdAtFrom', createdAtFrom)
  if (createdAtTo) params.set('createdAtTo', createdAtTo)
  const qs = params.toString()
  if (qs) url += `?${qs}`
  return await API.get(url)
}

export const createCsvReport = async (from: string, to: string) => {
  return await API.post('taxation/createCsvReport', { from, to })
}

export const getStatusCsvReport = async (id: number) => {
  return await API.get(`taxation/getStatusCsvReport/${id}`)
}

export const downloadCsvReport = async (id: number) => {
  return axios.get(`${apiAddress}/taxation/downloadCsvReport/${id}`, {
    withCredentials: true,
    responseType: 'blob',
  })
}

export const fetchOrderCsvReports = async (
  page: number,
  createdAtFrom?: string,
  createdAtTo?: string
) => {
  let url = `user/order/fetchCsvReports/${page}`
  const params = new URLSearchParams()
  if (createdAtFrom) params.set('createdAtFrom', createdAtFrom)
  if (createdAtTo) params.set('createdAtTo', createdAtTo)
  const qs = params.toString()
  if (qs) url += `?${qs}`
  return await API.get(url)
}

export const createOrderCsvReport = async (
  from: string,
  to: string,
  currency?: string,
  salesChannel?: string,
  exportType?: string
) => {
  return await API.post('user/order/createCsvReport', {
    from,
    to,
    currency,
    salesChannel,
    exportType,
  })
}

export const getStatusOrderCsvReport = async (id: number) => {
  return await API.get(`user/order/getStatusCsvReport/${id}`)
}

export const downloadOrderCsvReport = async (id: number) => {
  return axios.get(`${apiAddress}/user/order/downloadCsvReport/${id}`, {
    withCredentials: true,
    responseType: 'blob',
  })
}

export const fetchTransactionCsvReports = async (
  page: number,
  createdAtFrom?: string,
  createdAtTo?: string
) => {
  let url = `inventoryTransaction/fetchCsvReports/${page}`
  const params = new URLSearchParams()
  if (createdAtFrom) params.set('createdAtFrom', createdAtFrom)
  if (createdAtTo) params.set('createdAtTo', createdAtTo)
  const qs = params.toString()
  if (qs) url += `?${qs}`
  return await API.get(url)
}

export const createTransactionCsvReport = async (
  from: string,
  to: string,
  channel: string,
  exportType: string
) => {
  return await API.post('inventoryTransaction/createCsvReport', { from, to, channel, exportType })
}

export const getStatusTransactionCsvReport = async (id: number) => {
  return await API.get(`inventoryTransaction/getStatusCsvReport/${id}`)
}

export const downloadTransactionCsvReport = async (id: number) => {
  return axios.get(`${apiAddress}/inventoryTransaction/downloadCsvReport/${id}`, {
    withCredentials: true,
    responseType: 'blob',
  })
}

export const fetchInventoryCsvReports = async (
  page: number,
  createdAtFrom?: string,
  createdAtTo?: string
) => {
  let url = `user/inventory/fetchCsvReports/${page}`
  const params = new URLSearchParams()
  if (createdAtFrom) params.set('createdAtFrom', createdAtFrom)
  if (createdAtTo) params.set('createdAtTo', createdAtTo)
  const qs = params.toString()
  if (qs) url += `?${qs}`
  return await API.get(url)
}

export const createInventoryCsvReport = async (idGame?: string) => {
  return await API.post('user/inventory/createCsvReport', { idGame })
}

export const getStatusInventoryCsvReport = async (id: number) => {
  return await API.get(`user/inventory/getStatusCsvReport/${id}`)
}

export const downloadInventoryCsvReport = async (id: number) => {
  return axios.get(`${apiAddress}/user/inventory/downloadCsvReport/${id}`, {
    withCredentials: true,
    responseType: 'blob',
  })
}

export const getPurchasesChangeLogs = async (page: number) => {
  return await API.get(`taxation/getPurchasesChangeLogs/${page}`)
}

export const getUploadedReceipt = async (id: number) => {
  return await API.get(`taxation/getUploadedReceipt/${id}`)
}

export const uploadReceipt = async (
  purchaseEventId: number,
  fileName: string,
  contentType: string,
  fileContent: string // dataURL
) => {
  const res = await API.post('taxation/uploadReceipt', {
    purchaseEventId,
    fileName,
    contentType,
    fileContent,
  })
  return res.data
}

export const deleteReceipt = async (purchaseEventId: number) => {
  return await API.delete(`taxation/deleteReceipt/${purchaseEventId}`)
}

export const downloadReceipt = async (purchaseEventId: number) => {
  const res = await axios.get(`${apiAddress}/taxation/downloadReceipt/${purchaseEventId}`, {
    withCredentials: true,
    responseType: 'blob',
  })

  const disposition = res.headers['content-disposition'] as string | undefined
  let filename = 'receipt'

  if (disposition) {
    const match = /filename="?([^"]+)"?/i.exec(disposition)
    if (match && match[1]) {
      filename = match[1]
    }
  }

  const blob = new Blob([res.data], {
    type: res.headers['content-type'] || 'application/octet-stream',
  })
  const url = window.URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}
